import Headroom from 'headroom.js';
import { getClosest, getAdjacentEl, generateId } from '../lib/lib';

const SITE_HEADER_ID = 'js-site-header';
const SITE_HEADER_CLASS = 'l-site-header';
const HEADROOM_DISABLED = '[data-headroom="disabled"]';
const HEADROOM_TOP = 'l-site-header--top';
const HEADROOM_UNPINNED = 'l-site-header--unpinned';

const siteHeaderElement = () => document.getElementById(SITE_HEADER_ID);

const options = {
  dataMenu: '[data-megamenu]',
  dataItem: '[data-megamenu-item]',
  dataBtn: '[data-megamenu-button]',
  megaMenuCls: '.mega-menu',
  topMenuCls: '.menu__item',
  isOpen: 'mega-menu--is-open',
};

export const menu = {
  init(userConfig = {}) {
    this.settings = Object.assign({}, options, userConfig);

    // DOM elements
    this.menu = document.querySelector(this.settings.dataMenu);
    if (this.menu !== null) {
      this.buttons = [...this.menu.querySelectorAll(this.settings.dataBtn)];
    }

    // Bind methods
    this.handleToggle = this.handleToggle.bind(this);
    this.handleKeyup = this.handleKeyup.bind(this);
    this.handleClickOutsideMenu = this.handleClickOutsideMenu.bind(this);
    this.setup = this.setup.bind(this);
    this.destroy = this.destroy.bind(this);
    this.isMenuOpen = this.isMenuOpen.bind(this);

    // Setup aria attributes
    this.setup(this.buttons ? this.buttons : []);
    this.addEventListeners();

    const disableHeadroom = !!document.querySelector(`${HEADROOM_DISABLED}`);

    if (siteHeaderElement() && !disableHeadroom) {
      const headroom = new Headroom(siteHeaderElement(), {
        offset: 130,
        tolerance: {
          up: 10,
          down: 0,
        },
        classes: {
          // when element is initialised
          initial: `${SITE_HEADER_CLASS}`,
          // when scrolling up
          pinned: `${SITE_HEADER_CLASS}--pinned`,
          // when scrolling down
          unpinned: `${SITE_HEADER_CLASS}--unpinned`,
          // when above offset
          top: `${SITE_HEADER_CLASS}--top`,
          // when below offset
          notTop: `${SITE_HEADER_CLASS}--not-top`,
          // when at bottom of scoll area
          bottom: `${SITE_HEADER_CLASS}--bottom`,
          // when not at bottom of scroll area
          notBottom: `${SITE_HEADER_CLASS}--not-bottom`,
          // when frozen method has been called
          frozen: `${SITE_HEADER_CLASS}--frozen`,
        },
      });

      headroom.init();
    }
  },

  addEventListeners() {
    if (this.menu !== null) {
      this.menu.addEventListener('click', this.handleToggle);
    }
  },

  removeEventListeners() {
    document.removeEventListener('keyup', this.handleKeyup);
    document.removeEventListener('click', this.handleClickOutsideMenu);
  },

  setup(buttons) {
    buttons.forEach(el => {
      const id = generateId(el, 'mega-menu-');
      const megaMenu = getAdjacentEl(el, this.settings.megaMenuCls, 1);

      if (megaMenu) {
        el.setAttribute('id', id);
        el.setAttribute('aria-controls', id);
        el.setAttribute('aria-expanded', false);

        megaMenu.setAttribute('aria-labelledby', id);
        megaMenu.setAttribute('aria-hidden', true);
      }
    });
  },

  destroy(buttons) {
    if (this.menu !== null) {
      this.menu.removeEventListener('click', this.handleToggle);
    }
    this.isMenuOpen('close');
    this.removeEventListeners();

    buttons.forEach(el => {
      const megaMenu = getAdjacentEl(el, this.settings.megaMenuCls, 1);
      el.removeAttribute('id');
      el.removeAttribute('aria-controls');
      el.removeAttribute('aria-expanded');

      if (megaMenu) {
        megaMenu.removeAttribute('aria-labelledby');
        megaMenu.removeAttribute('aria-hidden');
      }
    });
  },

  handleKeyup(event) {
    const escapeKey = 27;

    if (event.which === escapeKey) {
      this.closeAll(this.buttons);
      this.removeEventListeners();
      this.isMenuOpen('close');
    }
  },

  handleClickOutsideMenu(event) {
    const target = event.target;
    const findEl = this.settings.topMenuCls;
    const closestEl = getClosest(target, findEl);
    const hasMegamenu = closestEl
      ? closestEl.querySelector(this.settings.megaMenuCls)
      : false;

    if (!closestEl || !hasMegamenu) {
      this.closeAll(this.buttons);
      this.removeEventListeners();
      this.isMenuOpen('close');
    }
  },

  toggleAria(attribute, el) {
    el.setAttribute(
      attribute,
      el.getAttribute(attribute) === 'true' ? false : true,
    );
  },

  open(el) {
    const megaMenu = getAdjacentEl(el, this.settings.megaMenuCls, 1);

    if (megaMenu) {
      this.toggleAria('aria-expanded', el);
      this.toggleAria('aria-hidden', megaMenu);
    }
  },

  closeAll(elements) {
    elements.forEach(el => {
      const megaMenu = getAdjacentEl(el, this.settings.megaMenuCls, 1);
      if (megaMenu) {
        el.setAttribute('aria-expanded', false);
        megaMenu.setAttribute('aria-hidden', true);
      }
    });
  },

  isOpen(elements) {
    return elements.some(el => el.getAttribute('aria-expanded') === 'true');
  },

  isMenuOpen(action) {
    const body = document.querySelector('body');
    const menuIsOpen = this.settings.isOpen;
    const header = document.querySelector('header');
    const freezeHeader = 'frozen';

    if (action === 'open') {
      body.classList.add(menuIsOpen);
      header.classList.add(freezeHeader);
    } else if (action === 'close') {
      body.classList.remove(menuIsOpen);
      header.classList.remove(freezeHeader);
    }
  },

  handleToggle(event) {
    const target = event.target;
    const buttons = this.buttons;

    if (!Element.prototype.matches) {
      Element.prototype.matches =
        Element.prototype.matchesSelector ||
        Element.prototype.mozMatchesSelector ||
        Element.prototype.msMatchesSelector ||
        Element.prototype.oMatchesSelector ||
        Element.prototype.webkitMatchesSelector ||
        function (s) {
          var matches = (this.document || this.ownerDocument).querySelectorAll(
              s,
            ),
            i = matches.length;
          while (--i >= 0 && matches.item(i) !== this) {}
          return i > -1;
        };
    }

    if (!target.matches(this.settings.dataBtn)) return;

    buttons.forEach(el => {
      if (target === el) {
        this.open(el);
      } else {
        this.closeAll([el]);
      }
    });

    if (this.isOpen(buttons)) {
      this.isMenuOpen('open');
      document.addEventListener('keyup', this.handleKeyup);
      document.addEventListener('click', this.handleClickOutsideMenu);
    } else {
      this.isMenuOpen('close');
      this.removeEventListeners();
    }
  },
};
